/**
 * Helpers for race cards (prize money, Group, class, race name, distance)
 * from /api/race-cards. Track keys match api/_race-cards.js normalizeTrack.
 */

export const BIG_RACE_PRIZE_MONEY = 200000;

export const normalizeTrack = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

/** The card for one race, or null. `cards` is the `data` of /api/race-cards. */
export function findRaceCard(cards, track, raceNumber) {
  const races = cards?.[normalizeTrack(track)]?.races;
  if (!races) return null;
  return races[String(Number(raceNumber))] || null;
}

/** 30000 -> "$30,000" (null when missing or 0). */
export function formatPrizeMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `$${Math.round(n).toLocaleString("en-AU")}`;
}

/** "Group 1" -> "G1"; "Listed" stays; "" -> "". */
export const shortGroup = (group) => {
  const m = /^Group\s*(\d)$/i.exec(String(group || "").trim());
  return m ? `G${m[1]}` : String(group || "").trim();
};

/** The class to show: the Group when there is one, else PF's class. */
export const raceClassLabel = (card) => card?.group || card?.raceClass || null;

/**
 * A horse's career earnings for Extra Details: 16900 -> "$16,900",
 * 0 -> "$0", "$286,300" (old PF format) unchanged, missing -> "N/A".
 */
export function formatCareerEarnings(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "string" && value.trim().startsWith("$"))
    return value.trim();
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `$${Math.round(n).toLocaleString("en-AU")}`;
}

/**
 * The Schedule's race filter: every race on the given tracks whose card
 * passes `filter` ("big" = $200k+, "group" = Group or Listed), in jump order.
 * `tracks` are the Schedule's tracks ({ name, races: [{ number, data }] }).
 */
export function filterRacesByCard(tracks, cards, filter) {
  const pass =
    filter === "big"
      ? (card) => (card.prizeMoney || 0) >= BIG_RACE_PRIZE_MONEY
      : filter === "group"
        ? (card) => !!card.group
        : null;
  if (!pass) return [];
  const out = [];
  for (const track of tracks || []) {
    for (const race of track?.races || []) {
      const card = findRaceCard(cards, track.name, race.number);
      if (card && pass(card)) out.push({ track, race, card });
    }
  }
  const jump = (r) => {
    const t = Date.parse(r.race?.data?.marketStartTime || "");
    return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER;
  };
  return out.sort(
    (a, b) => jump(a) - jump(b) || a.track.name.localeCompare(b.track.name),
  );
}
